/**
 * Validaciones reutilizables del Panel Comerciales. Son funciones puras para
 * facilitar pruebas unitarias y composición desde formularios y modales.
 */

const EMAIL_LOCAL_RE = /^[^\s@]{1,64}$/;
const EMAIL_DOMAIN_RE = /^[^\s@]{1,253}$/;
const EMAIL_DOMAIN_HAS_DOT_RE = /^[^\s@.]+\.[^\s@.]+/;
const CURRENCY_RE = /^\$?\s?\d{1,3}(?:,?\d{3})*(?:\.\d{1,2})?$/;
const MAX_EMAIL_LENGTH = 254;

export const isRequired = (v: string | undefined | null): boolean =>
    typeof v === 'string' && v.trim().length > 0;

/**
 * Validador de email en dos fases (local part + domain) para evitar regex con
 * backtracking exponencial (ReDoS) según S5852.
 */
export const isEmail = (v: string): boolean => {
    const trimmed = v.trim();
    if (trimmed.length === 0 || trimmed.length > MAX_EMAIL_LENGTH) return false;
    const at = trimmed.indexOf('@');
    if (at <= 0 || at !== trimmed.lastIndexOf('@')) return false;
    const local = trimmed.slice(0, at);
    const domain = trimmed.slice(at + 1);
    return (
        EMAIL_LOCAL_RE.test(local) &&
        EMAIL_DOMAIN_RE.test(domain) &&
        EMAIL_DOMAIN_HAS_DOT_RE.test(domain)
    );
};

export const isCurrency = (v: string): boolean => CURRENCY_RE.test(v.trim());

export const isCapacity = (v: number): boolean => v >= 0 && v <= 140;

export const isNonEmptyTeamName = (v: string): boolean =>
    isRequired(v) && v.trim().length <= 60;

export const isFutureOrTodayDate = (v: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(`${v}T00:00:00`);
    return !Number.isNaN(target.getTime()) && target.getTime() >= today.getTime();
};

export const validate = <T extends Record<string, unknown>>(
    data: T,
    rules: Partial<Record<keyof T, (val: unknown) => string | null>>,
): Partial<Record<keyof T, string>> => {
    const errors: Partial<Record<keyof T, string>> = {};
    (Object.keys(rules) as Array<keyof T>).forEach((key) => {
        const rule = rules[key];
        if (!rule) return;
        const error = rule(data[key]);
        if (error) errors[key] = error;
    });
    return errors;
};
